import { computed, inject, Injectable, Signal } from '@angular/core';
import {
  ICalGtmBaseEvent,
  IGtmAdapter,
  TCalGtmAuditRequestEvent,
  TCalGtmCheckboxClickEvent,
  TCalGtmFormSentEvent,
  TCalGtmFunnelClickEvent,
  TCalGtmFunnelEndEvent,
  TCalGtmFunnelStepEvent,
  TCalGtmQuestionFilledEvent,
  TCalGtmQuestionValidationEvent
} from '@cal-analytics/gtm';
import { IPartner } from '@partners/api';
import { AmountStore, ContactStore, IdentityStore, PurposeStore, ShellStore, SimulatorStore } from '@partners/state';

@Injectable({
  providedIn: 'root'
})
export class GtmAdapterService implements IGtmAdapter {
  readonly partner$$: Signal<IPartner>;
  readonly isLoanProcess$$: Signal<boolean>;
  readonly isCardProcess$$: Signal<boolean>;

  protected readonly _simulatorStore = inject(SimulatorStore);
  protected readonly _purposeStore = inject(PurposeStore);
  protected readonly _shellStore = inject(ShellStore);
  protected readonly _amountStore = inject(AmountStore);
  protected readonly _identityStore = inject(IdentityStore);
  protected readonly _contactStore = inject(ContactStore);

  funnelClickGetter: Signal<Partial<TCalGtmFunnelClickEvent>>;
  questionFilledGetter: Signal<Partial<TCalGtmQuestionFilledEvent>>;
  checkboxClickGetter: Signal<Partial<TCalGtmCheckboxClickEvent>>;
  questionValidationGetter: Signal<Partial<TCalGtmQuestionValidationEvent>>;
  calculateProposalGetter?: Signal<Partial<TCalGtmAuditRequestEvent>>;
  formSentGetter: Signal<Partial<TCalGtmFormSentEvent>>;

  private readonly processType$$: Signal<{ process_type: string }>;
  private readonly partnerName$$: Signal<{ partner_name: string }>;
  private readonly clientType$$: Signal<{ client_type: string }>;
  private readonly clientSubType$$: Signal<{ client_sub_type: string }>;
  protected readonly cardName$$: Signal<{ card_name: string }>;

  constructor() {
    this.isLoanProcess$$ = this._shellStore.matchLoan$$;
    this.isCardProcess$$ = this._shellStore.matchCard$$;

    this.partner$$ = computed<IPartner>(() => this._shellStore.profile$$()?.partner);

    this.processType$$ = computed(() => {
      if (this.isCardProcess$$()) {
        return {
          process_type: 'sigma card order'
        };
      }

      if (this.isLoanProcess$$()) {
        return {
          process_type: 'sigma loan'
        };
      }

      return { process_type: 'NA' };
    });

    this.partnerName$$ = computed(() => ({ partner_name: this.partner$$().name }));

    this.clientSubType$$ = computed(() => {
      const data = this.getCustomerTypeData();

      if (data?.customerType == 2) {
        return { client_sub_type: 'בנקאי' };
      } else if (data?.customerType == 3) {
        return { client_sub_type: 'דואלי' };
      } else if (data?.customerType == 4) {
        return { client_sub_type: 'מנפיק' };
      }

      return { client_sub_type: 'NA' };
    });

    this.clientType$$ = computed(() => {
      const data = this.getCustomerTypeData();

      if (this.isNewOrUnknownCustomerType(data?.customerType)) {
        return { client_type: 'new' };
      } else if (this.isExistCustomerType(data?.customerType)) {
        if (data?.isVerifiedCustomer) {
          return { client_type: 'existing identified' };
        } else {
          return { client_type: 'existing unidentified' };
        }
      }

      return { client_type: 'NA' };
    });

    this.cardName$$ = computed(() => {
      if (this.isCardProcess$$()) {
        return { card_name: this._shellStore.profile$$()?.partner.name };
      } else {
        return { card_name: 'NA' };
      }
    });
  }

  funnelStepGetter: Signal<Partial<TCalGtmFunnelStepEvent>>;
  funnelEndGetter: Signal<Partial<TCalGtmFunnelEndEvent>>;
  auditRequestGetter?: Signal<Partial<TCalGtmAuditRequestEvent>>;

  baseEventGetter: Signal<Partial<ICalGtmBaseEvent>> = computed(() => ({
    ...this.processType$$(),
    ...this.partnerName$$(),
    ...this.clientType$$(),
    ...this.clientSubType$$(),
    ...(this.isCardProcess$$() ? { card_name: this.cardName$$().card_name } : {})
  }));

  isNewOrUnknownCustomerType(customerType?: number): boolean {
    return customerType == 0 || customerType == 1;
  }

  isExistCustomerType(customerType?: number): boolean {
    return customerType == 2 || customerType == 3 || customerType == 4;
  }

  getCustomerTypeData(): { customerType?: number; isVerifiedCustomer: boolean } {
    const identityData = this._identityStore.data$$();
    const contactData = this._contactStore.data$$();

    if (identityData?.customerType != null) {
      return {
        customerType: identityData.customerType,
        isVerifiedCustomer: identityData.isVerifiedCustomer
      };
    } else if (contactData?.customerType != null) {
      return {
        customerType: contactData.customerType,
        isVerifiedCustomer: contactData.isVerifiedCustomer
      };
    } else {
      return {
        customerType: null,
        isVerifiedCustomer: false
      };
    }
  }
}
